import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { COMPANY } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    toast({ title: 'Message sent', description: "We'll get back to you shortly." });
    setForm({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="bg-white">
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-slate-900">Contact Us</h1>
          <p className="mt-3 text-slate-600">We’re here to help. Reach out with any questions about our products or your order.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center text-[#5d728c]">
              <Phone className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm uppercase tracking-wide text-slate-500">Phone</p>
              <a href={`tel:${COMPANY.phone}`} className="text-slate-900 font-medium hover:text-[#5d728c]">{COMPANY.phone}</a>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center text-[#5d728c]">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm uppercase tracking-wide text-slate-500">Email</p>
              <a href={`mailto:${COMPANY.email}`} className="text-slate-900 font-medium hover:text-[#5d728c] break-all">{COMPANY.email}</a>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center text-[#5d728c]">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm uppercase tracking-wide text-slate-500">Address</p>
              <p className="text-slate-900 font-medium">{COMPANY.address}</p>
              <p className="text-slate-500 text-sm mt-1">{COMPANY.corp}</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center text-[#5d728c]">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm uppercase tracking-wide text-slate-500">Return Address</p>
              <p className="text-slate-900 font-medium">{COMPANY.returnAddress}</p>
              <p className="text-slate-500 text-sm mt-1">Please contact us before shipping any return.</p>
            </div>
          </div>

          <div className="mt-8 bg-slate-50 rounded-lg p-5 text-sm text-slate-600 leading-relaxed">
            <p className="font-medium text-slate-800">Business Hours</p>
            <p>Monday – Friday: 9:00 AM – 6:00 PM (CT)</p>
            <p>Saturday: 10:00 AM – 2:00 PM (CT)</p>
            <p>Sunday: Closed</p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 md:p-8">
          <h2 className="font-serif text-2xl text-slate-900">Send Us a Message</h2>
          <p className="text-slate-600 text-sm mt-1">We typically respond within one business day.</p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" required value={form.name} onChange={update('name')} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={update('email')} className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="subject">Subject</Label>
              <Input id="subject" required value={form.subject} onChange={update('subject')} className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="message">Message</Label>
              <Textarea id="message" required rows={6} value={form.message} onChange={update('message')} className="mt-1" />
            </div>
          </div>

          <Button type="submit" className="mt-6 bg-[#5d728c] hover:bg-[#4d6278] rounded-full px-7">
            Send Message <Send className="ml-2 w-4 h-4" />
          </Button>
          {sent && <p className="text-emerald-700 text-sm mt-3">Thanks! Your message has been sent.</p>}
        </form>
      </div>
    </div>
  );
}
